import React, { useEffect, useId, useState } from "react";
import WrapperInput, { IWrapperInput } from "./WrapperInput";
import {
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { cloneDeep, isArray } from "lodash";
import { v4 } from "uuid";
import { LuChevronDown, LuXCircle } from "react-icons/lu";

export interface ITextTagInput extends Omit<IWrapperInput, "children"> {
  placeholder?: string;
  errorMessage?: string | undefined;
  disabled?: boolean;
  readOnly?: boolean;
  endAdornment?: React.ReactNode;
  textFieldProps?: TextFieldProps;
  pending?: boolean;
  defaultValue?: string[];
  toggleClear?: number;
  onChange?: (tags: string[], merge: boolean) => void;
}

const heights = {
  input: "42px",
};

function TextTagInput({
  id,
  label,
  labelWidth = "20%",
  defaultValue,
  placeholder,
  required,
  errorMessage,
  disabled,
  readOnly,
  endAdornment,
  pending,
  textFieldProps,
  toggleClear,
  onChange,
  ...wrapperProps
}: ITextTagInput) {
  const idLabel = useId();
  const [text, setText] = useState<string>("");
  const [tags, setTags] = useState<string[]>([]);
  const [merge, setMerge] = useState<boolean>(false);
  const [anchorMenu, setAnchorMenu] = useState<HTMLElement | null>(null);

  const handleChangeMode = (newMerge: boolean) => {
    onChange?.(tags, newMerge);
    setMerge(newMerge);
    setAnchorMenu(null);
  };

  const handleSaveTag = () => {
    if (!text || pending) return;
    const newTags = [...(tags || []), text];
    setTags?.(newTags);
    setText("");
    onChange?.(newTags, merge);
  };

  const handleDeleteTag = (index: number) => {
    if (pending) return;
    const tagsClone = cloneDeep(tags || []);
    tagsClone?.splice(index, 1);
    setTags?.(tagsClone);
    onChange?.(tagsClone, merge);
  };

  const handleClear = () => {
    setTags([]);
    onChange?.([], merge);
  };

  useEffect(() => {
    setTags([]);
  }, [toggleClear]);

  return (
    <WrapperInput
      id={idLabel || id}
      label={label}
      required={required}
      labelWidth={labelWidth}
      {...wrapperProps}
    >
      <Stack direction="row" alignItems="center" gap={0.5}>
        <Stack
          gap={0.5}
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          sx={{
            flex: 1,
            width: "100%",
            border: "2px solid",
            borderColor: "divider",
            borderRadius: "10px",
            px: 1,
            py: 0.5,
            "&:focus-within": {
              borderColor: "primary.main",
            },
          }}
        >
          {isArray(tags) &&
            tags?.length > 0 &&
            tags.map((tag, index) => {
              return (
                <Chip
                  key={v4()}
                  label={tag}
                  onDelete={() => handleDeleteTag(index)}
                />
              );
            })}
          <TextField
            id={idLabel || id}
            variant="outlined"
            fullWidth
            value={text}
            onChange={(e) => setText(e.target.value as string)}
            placeholder={placeholder}
            autoComplete="off"
            spellCheck={false}
            helperText={errorMessage}
            slotProps={{
              input: {
                readOnly,
                disabled,
                endAdornment: (
                  <>
                    {isArray(tags) && tags?.length > 0 && (
                      <IconButton onClick={handleClear}>
                        <LuXCircle size={16} />
                      </IconButton>
                    )}
                  </>
                ),
              },
            }}
            onKeyUp={(e) => {
              if (e.which === 13) {
                handleSaveTag();
              }
            }}
            sx={{
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: "40%",
              "& .MuiFormHelperText-root": {
                color: "error.main",
                fontStyle: "italic",
              },
              "& .MuiInputBase-root": {
                height: heights.input,
                px: 0,
                py: 0,
                "& .MuiInputBase-input": {
                  height: heights.input,
                  px: 0,
                  py: 1,
                },
                "& fieldset": { border: "none" },
              },
            }}
          />
        </Stack>
        <Button
          onClick={(e) => setAnchorMenu(e.currentTarget)}
          disabled={pending}
          variant="contained"
          endIcon={<LuChevronDown size={14} />}
          sx={{
            backgroundColor: "background.default",
            boxShadow: "none",
            textTransform: "none",
            px: 1,
            minWidth: "unset",
            width: "60px",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {merge ? "AND" : "OR"}
        </Button>
        <Menu
          anchorEl={anchorMenu}
          open={!!anchorMenu}
          onClose={() => setAnchorMenu(null)}
          slotProps={{ paper: { elevation: 1 } }}
        >
          <MenuItem disabled={merge} onClick={() => handleChangeMode(true)}>
            AND
          </MenuItem>
          <MenuItem disabled={!merge} onClick={() => handleChangeMode(false)}>
            OR
          </MenuItem>
        </Menu>
      </Stack>
    </WrapperInput>
  );
}

export default TextTagInput;
